import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/**
 * Capitaliza a primeira letra de cada palavra
 * Exemplo: "evento futebol" → "Evento Futebol"
 */
export const capitalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Formata preço em reais
 * Exemplo: 50 → "R$ 50,00"
 */
export const formatPrice = (price) => {
  if (!price || price === 0) return 'Gratuito';
  return `R$ ${price.toFixed(2).replace('.', ',')}`;
};

/**
 * Extrai o horário de um evento.
 * Prioridade: startDate (se não for meia-noite) → time string → fallback.
 */
export const getEventTime = (event) => {
  if (event?.startDate) {
    const date = event.startDate.toDate ? event.startDate.toDate() : new Date(event.startDate);
    const h = date.getHours();
    const m = date.getMinutes();
    if (h !== 0 || m !== 0) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  if (event?.time && event.time !== '') return event.time;
  return 'Horário não definido';
};

/**
 * Resolve a data/hora real de início do evento.
 * Eventos novos (app Flutter e painel web padronizado) já têm a hora
 * embutida em startDate/date. Eventos antigos do painel web têm o
 * Timestamp zerado (00:00:00) e a hora separada no campo "time" —
 * mantido aqui apenas como fallback de leitura, sem dependência na escrita.
 */
export const getEventDateTime = (event) => {
  const base = event?.startDate || event?.date;
  if (!base) return null;

  const baseDate = base.toDate ? base.toDate() : new Date(base);
  if (isNaN(baseDate.getTime())) return null;

  if (baseDate.getHours() !== 0 || baseDate.getMinutes() !== 0) {
    return baseDate;
  }

  let hours = 0;
  let minutes = 0;
  if (typeof event?.time === 'string' && event.time.includes(':')) {
    const [h, m] = event.time.split(':').map(Number);
    if (!Number.isNaN(h)) hours = h;
    if (!Number.isNaN(m)) minutes = m;
  }

  return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes, 0, 0);
};

/**
 * Resolve o instante real de término do evento (endDate, com fallback para
 * date quando endDate não existir).
 */
export const getEventEndDateTime = (event) => {
  const end = event?.endDate || event?.date;
  if (!end) return null;

  const endDate = end.toDate ? end.toDate() : new Date(end);
  if (isNaN(endDate.getTime())) return null;

  return endDate;
};

// Tolerância para absorver arredondamento de segundos/milissegundos na
// comparação now <= endDate.
const ACTIVE_GRACE_PERIOD_MS = 60 * 1000;

/**
 * Um evento é "ativo" (ainda não encerrado) enquanto now <= endDate + 1 minuto.
 * Baseado em endDate — não mais em startDate — já que o sistema agora grava
 * o horário real de término do evento.
 */
export const isEventActive = (event) => {
  const endDateTime = getEventEndDateTime(event);
  if (!endDateTime) return false;
  return Date.now() <= endDateTime.getTime() + ACTIVE_GRACE_PERIOD_MS;
};

/**
 * Geocodifica um endereço via Cloud Function (Google Geocoding API).
 * A chave da API vive exclusivamente no backend (Firebase Secret Manager) —
 * o frontend apenas invoca a Cloud Function autenticada.
 * Retorna latitude e longitude.
 */
const geocodeAddressCallable = httpsCallable(functions, 'geocodeAddress');

export const geocodeAddress = async (address, city) => {
  // TEMP DEBUG — remover após identificar a causa do fallback.
  console.log('[DEBUG geocodeAddress] enviando para a callable:', { address, city });
  try {
    const result = await geocodeAddressCallable({ address, city });
    // TEMP DEBUG
    console.log('[DEBUG geocodeAddress] result.data recebido:', result.data);
    return result.data;
  } catch (error) {
    // TEMP DEBUG
    console.log('[DEBUG geocodeAddress] callable LANÇOU exceção:', error?.code, error?.message, error);
    console.error('Geocoding error:', error);
    // Fallback para Joinville
    return {
      latitude: -26.3045,
      longitude: -48.8487,
      success: false
    };
  }
};

